import { IndexTable, Card, useIndexResourceState } from '@shopify/polaris';
import { ReactNode } from 'react';
import { EmptyState } from './EmptyState';
import { LoadingState } from './LoadingState';

export interface DataTableColumn {
  id: string;
  title: string;
}

export interface DataTableRow {
  id: string;
  cells: ReactNode[];
}

export interface DataTableProps {
  columns: DataTableColumn[];
  rows: DataTableRow[];
  loading?: boolean;
  emptyState?: { heading: string; children?: ReactNode };
  resourceName?: { singular: string; plural: string };
}

export function DataTable({
  columns,
  rows,
  loading,
  emptyState,
  resourceName,
}: DataTableProps): JSX.Element {
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(rows.map((r) => ({ id: r.id })));

  if (loading) {
    return (
      <Card>
        <LoadingState message="Loading..." />
      </Card>
    );
  }

  if (rows.length === 0 && emptyState) {
    return (
      <Card>
        <EmptyState heading={emptyState.heading}>{emptyState.children}</EmptyState>
      </Card>
    );
  }

  return (
    <Card padding="0">
      <IndexTable
        resourceName={resourceName ?? { singular: 'row', plural: 'rows' }}
        itemCount={rows.length}
        selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
        onSelectionChange={handleSelectionChange}
        selectable={false}
        headings={columns.map((c) => ({ title: c.title })) as [{ title: string }, ...{ title: string }[]]}
      >
        {rows.map((row, index) => (
          <IndexTable.Row
            id={row.id}
            key={row.id}
            position={index}
            selected={selectedResources.includes(row.id)}
          >
            {row.cells.map((cell, i) => (
              <IndexTable.Cell key={`${row.id}-${i}`}>{cell}</IndexTable.Cell>
            ))}
          </IndexTable.Row>
        ))}
      </IndexTable>
    </Card>
  );
}
